import { Edit3, Trash2, CheckCircle, Clock, PauseCircle, XCircle } from 'lucide-react';
import { formatCurrency, formatDate } from '../utils/formatters';

const STATUS_CONFIG = {
  active: {
    icon: <Clock className="w-3.5 h-3.5" />,
    label: 'Active',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-100',
    bar: 'bg-blue-500',
  },
  completed: {
    icon: <CheckCircle className="w-3.5 h-3.5" />,
    label: 'Completed',
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-100',
    bar: 'bg-green-500',
  },
  paused: {
    icon: <PauseCircle className="w-3.5 h-3.5" />,
    label: 'Paused',
    color: 'text-yellow-600',
    bg: 'bg-yellow-50',
    border: 'border-yellow-100',
    bar: 'bg-yellow-500',
  },
  cancelled: {
    icon: <XCircle className="w-3.5 h-3.5" />,
    label: 'Cancelled',
    color: 'text-red-500',
    bg: 'bg-red-50',
    border: 'border-red-100',
    bar: 'bg-red-400',
  },
};

/**
 * GoalCard — displays a single investment goal with progress bar and actions.
 */
const GoalCard = ({ goal, onEdit, onDelete, onMarkComplete }) => {
  const config = STATUS_CONFIG[goal.status] || STATUS_CONFIG.active;
  const availableCash = parseFloat(goal.available_cash || 0);
  const totalInvested = parseFloat(goal.total_invested || 0);
  const portfolioValue = parseFloat(goal.portfolio_value || 0);
  const totalWealth = portfolioValue + availableCash;
  const targetMax = parseFloat(goal.target_amount_max || 0);
  const targetMin = parseFloat(goal.target_amount_min || 0);
  const progress = targetMax > 0 ? Math.min((totalWealth / targetMax) * 100, 100) : 0;

  return (
    <div className={`bg-white rounded-2xl shadow-sm border ${config.border} overflow-hidden hover:shadow-md transition-shadow`}>
      {/* Progress track at top */}
      <div className="h-1.5 w-full bg-gray-100">
        <div
          className={`h-full ${config.bar} transition-all duration-500 rounded-full`}
          style={{ width: `${Math.max(progress, progress > 0 ? 2 : 0)}%` }}
        />
      </div>

      <div className="p-5">
        {/* Header row */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0 pr-3">
            <h3 className="font-bold text-gray-900 text-lg leading-tight">{goal.name}</h3>
            {goal.description && (
              <p className="text-gray-500 text-xs mt-1 line-clamp-2">{goal.description}</p>
            )}
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {goal.status !== 'completed' && (
              <button
                onClick={onMarkComplete}
                className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-xl transition"
                title="Mark as completed"
                aria-label="Mark goal as completed"
              >
                <CheckCircle className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onEdit}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition"
              title="Edit goal"
              aria-label="Edit goal"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition"
              title="Delete goal"
              aria-label="Delete goal"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Status badge */}
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${config.bg} ${config.color} text-xs font-medium mb-4`}>
          {config.icon}
          {config.label}
        </div>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between items-baseline mb-1.5">
            <span className="text-sm font-semibold text-gray-900">
              {formatCurrency(totalWealth, goal.currency)}
            </span>
            <span className="text-xs text-gray-400">
              {formatCurrency(targetMin, goal.currency)} – {formatCurrency(targetMax, goal.currency)}
            </span>
          </div>

          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${config.bar}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-400">{progress.toFixed(1)}% of target</span>
            {goal.target_date && (
              <span className="text-xs text-gray-400">
                Target: {formatDate(goal.target_date, true)}
              </span>
            )}
          </div>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-2 gap-2.5 mb-2.5">
          <div className="bg-blue-50 rounded-xl p-3">
            <p className="text-xs text-blue-500 mb-1">Invested</p>
            <p className="text-sm font-bold text-blue-900">
              {formatCurrency(totalInvested, goal.currency)}
            </p>
          </div>
          <div className="bg-green-50 rounded-xl p-3">
            <p className="text-xs text-green-600 mb-1">Available</p>
            <p className="text-sm font-bold text-green-900">
              {formatCurrency(availableCash, goal.currency)}
            </p>
            <p className="text-xs text-green-500 mt-0.5">broker balance</p>
          </div>
          <div className="bg-indigo-50 rounded-xl p-3">
            <p className="text-xs text-indigo-500 mb-1">Portfolio</p>
            <p className="text-sm font-bold text-indigo-900">
              {formatCurrency(portfolioValue, goal.currency)}
            </p>
          </div>
          <div className="bg-amber-50 rounded-xl p-3">
            <p className="text-xs text-amber-600 mb-1">Total</p>
            <p className="text-sm font-bold text-amber-900">
              {formatCurrency(totalWealth, goal.currency)}
            </p>
            <p className="text-xs text-amber-500 mt-0.5">portfolio + cash</p>
          </div>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 flex justify-between items-center">
          <p className="text-xs text-gray-500">Transactions</p>
          <p className="text-sm font-bold text-gray-900">{goal.transactions_count || 0}</p>
        </div>
      </div>
    </div>
  );
};

export default GoalCard;
